/**
 * React Query hooks, one per endpoint. Each workspace subscribes only to what it renders, so a
 * screen's request count is visible from its imports — which is what the freeze checklist in the
 * review guide checks.
 */

import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from 'src/api/client';

import {
  createAlertRule,
  createChannel,
  createPerson,
  createSpeakerProfile,
  deleteAlertRule,
  deleteChannel,
  fetchAlertRules,
  fetchChannels,
  fetchCanonicalTopic,
  filterTopics,
  fetchCriteriaMatches,
  createFeedSource,
  deleteFeedSource,
  fetchEnrichments,
  fetchFeedItems,
  fetchFeedSources,
  pollFeedSource,
  fetchLlmCosts,
  previewFeedSource,
  updateFeedSource,
  fetchOutputs,
  fetchPeople,
  fetchPipelineJobs,
  fetchPrenotas,
  fetchRelevanceWatches,
  fetchReviews,
  fetchSession,
  fetchSignals,
  fetchSpeakerProfile,
  fetchSpeakerProfiles,
  fetchStoryCandidates,
  fetchStream,
  fetchStreamSummary,
  fetchStreams,
  fetchTopics,
  fetchTranscript,
  fetchVerifications,
  fetchXReviews,
  countAlertEventsSince,
  filterAlertEvents,
  resetPersonPassword,
  setPersonStatus,
  submitFeedback,
  submitPipelineJob,
  submitReview,
  submitSpeakerCommand,
  updateAlertRule,
  updateChannel,
  uploadSpeakerClip,
} from 'src/api/endpoints';
import type { EditorialAlertSurface, EditorialPrenota, EditorialSignal, EditorialStream, EditorialStreamSummary, PipelineJob } from 'src/types/editorial';

/** A completed broadcast never changes; a live one does. Staleness is decided per workspace. */
const STABLE = {
  staleTime: 5 * 60_000,
  /**
   * A 404 or a 400 answers the same way however many times it is asked, and the client already
   * bounds each attempt at 30s — three silent retries would leave an editor watching a skeleton
   * for a minute and a half before being told anything. Retry once, for the transient case.
   */
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
    return failureCount < 1;
  },
} as const;

/**
 * How often a workspace re-reads a broadcast that is still being captured.
 *
 * Fifteen seconds, against a pipeline that closes an editorial chunk every ninety: fast enough
 * that a line appears while the sentence is still in the room's memory, slow enough that four
 * open tabs are four requests a minute each rather than a poll storm against one Lambda.
 */
const LIVE_POLL_MS = 15_000;

const LIVE_STATUSES = new Set(['live', 'reprocessing']);

/**
 * The freshness a stream's artifacts deserve *right now*.
 *
 * A finished broadcast is immutable — five minutes of staleness costs nothing and saves every
 * workspace a request per mount. A live one grows a chunk at a time underneath the reader, and
 * before this every screen but the queue showed whatever had been fetched when it opened: an
 * editor watching the mañanera saw the transcript stop and had to reload to find out it had not.
 *
 * Read from the cache rather than subscribed to: the page that renders these workspaces is already
 * subscribed through `useStream`, so a broadcast ending re-renders it and this is re-evaluated
 * with it.
 */
function useStreamFreshness(streamId: string) {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<{ stream: EditorialStream }>(['stream', streamId]);

  if (!cached || !LIVE_STATUSES.has(cached.stream.status)) return STABLE;
  return { staleTime: 5_000, refetchInterval: LIVE_POLL_MS, retry: STABLE.retry } as const;
}

export function useStreams() {
  return useQuery({
    queryKey: ['streams'],
    queryFn: () => fetchStreams(),
    staleTime: 30_000,
    // The list carries the badge that says a broadcast is on air, so it keeps looking while one is.
    refetchInterval: query => (query.state.data?.streams.some(stream => LIVE_STATUSES.has(stream.status)) ? LIVE_POLL_MS : false),
  });
}

export function useStream(streamId: string) {
  return useQuery({
    queryKey: ['stream', streamId],
    queryFn: () => fetchStream(streamId),
    staleTime: 30_000,
    // This is the query every workspace reads its own freshness from, so it has to be the one
    // that notices the broadcast ending.
    refetchInterval: query => (LIVE_STATUSES.has(query.state.data?.stream.status ?? '') ? LIVE_POLL_MS : false),
  });
}

export function useTranscript(streamId: string) {
  return useQuery({ queryKey: ['transcript', streamId], queryFn: () => fetchTranscript(streamId), ...useStreamFreshness(streamId) });
}

export function useTopics(streamId: string) {
  return useQuery({ queryKey: ['topics', streamId], queryFn: () => fetchTopics(streamId), ...useStreamFreshness(streamId) });
}

export function useSignals(streamId: string) {
  return useQuery({ queryKey: ['signals', streamId], queryFn: () => fetchSignals(streamId), ...useStreamFreshness(streamId) });
}

export function useCriteriaMatches(streamId: string, matchedOnly = true) {
  return useQuery({
    queryKey: ['criteria-matches', streamId, matchedOnly],
    queryFn: () => fetchCriteriaMatches(streamId, { matchedOnly }),
    ...useStreamFreshness(streamId),
  });
}

export function useStoryCandidates(streamId: string) {
  return useQuery({
    queryKey: ['story-candidates', streamId],
    queryFn: () => fetchStoryCandidates(streamId),
    ...useStreamFreshness(streamId),
  });
}

export function usePrenotas(streamId: string) {
  return useQuery({ queryKey: ['prenotas', streamId], queryFn: () => fetchPrenotas(streamId), ...useStreamFreshness(streamId) });
}

export function useXReviews(streamId: string) {
  return useQuery({ queryKey: ['x-reviews', streamId], queryFn: () => fetchXReviews(streamId), ...useStreamFreshness(streamId) });
}

export function useVerifications(streamId: string) {
  return useQuery({ queryKey: ['verifications', streamId], queryFn: () => fetchVerifications(streamId), ...useStreamFreshness(streamId) });
}

export function useEnrichments(streamId: string) {
  return useQuery({ queryKey: ['enrichments', streamId], queryFn: () => fetchEnrichments(streamId), ...useStreamFreshness(streamId) });
}

export function useOutputs(streamId: string) {
  return useQuery({ queryKey: ['outputs', streamId], queryFn: () => fetchOutputs(streamId), ...useStreamFreshness(streamId) });
}

export function useReviews(streamId: string) {
  return useQuery({ queryKey: ['reviews', streamId], queryFn: () => fetchReviews(streamId), ...STABLE });
}

/** Records a verdict on one draft, then refetches the stream's reviews so it appears in place. */
export function useSubmitReview(streamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitReview,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reviews', streamId] }),
  });
}

/**
 * Lightweight verdicts on signals, prenotas and topic mentions. Nothing on screen changes when one
 * lands — the pipeline reads them on its next learning pass — so there is nothing to invalidate.
 */
export function useSubmitFeedback() {
  return useMutation({ mutationFn: submitFeedback });
}

/**
 * Everything the Claim Thread needs, in one subscription. Composition §12: the thread is assembled
 * client-side from payloads the stream already has, so this is a fan-out of five cached queries
 * rather than a new endpoint.
 */
export function useThreadSources(streamId: string) {
  const freshness = useStreamFreshness(streamId);
  const results = useQueries({
    queries: [
      { queryKey: ['signals', streamId], queryFn: () => fetchSignals(streamId), ...freshness },
      { queryKey: ['criteria-matches', streamId, true], queryFn: () => fetchCriteriaMatches(streamId), ...freshness },
      { queryKey: ['prenotas', streamId], queryFn: () => fetchPrenotas(streamId), ...freshness },
      { queryKey: ['verifications', streamId], queryFn: () => fetchVerifications(streamId), ...freshness },
      { queryKey: ['outputs', streamId], queryFn: () => fetchOutputs(streamId), ...freshness },
    ],
  });

  const [signals, matches, prenotas, verifications, outputs] = results;

  return {
    isPending: results.some(result => result.isPending),
    error: results.find(result => result.error)?.error ?? null,
    sources: {
      signals: signals?.data?.signals ?? [],
      criteriaMatches: matches?.data?.criteriaMatches ?? [],
      prenotas: prenotas?.data?.prenotas ?? [],
      verifications: verifications?.data?.verifications ?? [],
      outputs: outputs?.data?.outputs ?? [],
    },
  };
}

/**
 * Resolves cited `claim_id`s to their text and their verbatim line.
 *
 * `enabled` is off until a reader actually opens a claim marker: the signals payload for a full
 * mañanera is hundreds of claims, and the reader must stay cheap for the many people who open a
 * shared link and only read the piece.
 */
export function useClaimSources(streamId: string, enabled: boolean) {
  const results = useQueries({
    queries: [
      { queryKey: ['signals', streamId], queryFn: () => fetchSignals(streamId), enabled, ...STABLE },
      { queryKey: ['prenotas', streamId], queryFn: () => fetchPrenotas(streamId), enabled, ...STABLE },
    ],
  });

  const [signals, prenotas] = results;
  return {
    isPending: enabled && results.some(result => result.isPending),
    signals: signals?.data?.signals ?? EMPTY_SIGNALS,
    prenotas: prenotas?.data?.prenotas ?? EMPTY_PRENOTAS,
  };
}

/** Stable empties — a fresh `?? []` would break the memo that builds the resolver index. */
const EMPTY_SIGNALS: EditorialSignal[] = [];
const EMPTY_PRENOTAS: EditorialPrenota[] = [];

/** The Overview needs a count from every stage, so it is the one screen that fans out widely. */
/**
 * The counts, in one request. Prefer this over `useStageCounts` on any screen that only shows how
 * far a broadcast got — the latter downloads every artifact to measure it.
 */
export function useStreamSummary(streamId: string) {
  return useQuery({ queryKey: ['stream-summary', streamId], queryFn: () => fetchStreamSummary(streamId), ...STABLE });
}

/**
 * The counts for a page of broadcasts, one cheap aggregate request each.
 *
 * The roster used to say only when a broadcast aired, which is the one thing its title already
 * says. What an editor actually wants from an index is which day produced work — and the counts
 * are a single database aggregate per stream, not a download of its artifacts.
 */
export function useStreamSummaries(streamIds: string[]) {
  const results = useQueries({
    queries: streamIds.map(streamId => ({
      queryKey: ['stream-summary', streamId],
      queryFn: () => fetchStreamSummary(streamId),
      ...STABLE,
    })),
  });

  // Keyed by id rather than returned positionally: the roster groups by day and renders out of
  // order, and an index-based lookup there would attach one broadcast's counts to another.
  const byStream = new Map<string, EditorialStreamSummary>();
  results.forEach((result, index) => {
    const id = streamIds[index];
    const summary = result.data?.summary;
    if (id && summary) byStream.set(id, summary);
  });

  return { byStream, isPending: results.some(result => result.isPending) };
}

export function useStageCounts(streamId: string) {
  const freshness = useStreamFreshness(streamId);
  const results = useQueries({
    queries: [
      { queryKey: ['transcript', streamId], queryFn: () => fetchTranscript(streamId), ...freshness },
      { queryKey: ['signals', streamId], queryFn: () => fetchSignals(streamId), ...freshness },
      { queryKey: ['criteria-matches', streamId, true], queryFn: () => fetchCriteriaMatches(streamId), ...freshness },
      { queryKey: ['story-candidates', streamId], queryFn: () => fetchStoryCandidates(streamId), ...freshness },
      { queryKey: ['prenotas', streamId], queryFn: () => fetchPrenotas(streamId), ...freshness },
      { queryKey: ['x-reviews', streamId], queryFn: () => fetchXReviews(streamId), ...freshness },
      { queryKey: ['verifications', streamId], queryFn: () => fetchVerifications(streamId), ...freshness },
      { queryKey: ['outputs', streamId], queryFn: () => fetchOutputs(streamId), ...freshness },
      { queryKey: ['reviews', streamId], queryFn: () => fetchReviews(streamId), ...freshness },
    ],
  });

  const [transcript, signals, matches, candidates, prenotas, xReviews, verifications, outputs, reviews] = results;

  return {
    isPending: results.some(result => result.isPending),
    // Nine endpoints feed one ribbon of counts. Without this, a single dead one renders as a
    // legitimate zero next to eight real numbers, and a zero is what an editor acts on.
    error: results.find(result => result.error)?.error ?? null,
    transcript: transcript?.data?.transcript ?? null,
    signals: signals?.data?.signals ?? [],
    criteriaMatches: matches?.data?.criteriaMatches ?? [],
    storyCandidates: candidates?.data?.storyCandidates ?? [],
    prenotas: prenotas?.data?.prenotas ?? [],
    xReviews: xReviews?.data?.xReviews ?? [],
    verifications: verifications?.data?.verifications ?? [],
    outputs: outputs?.data?.outputs ?? [],
    reviews: reviews?.data?.reviews ?? [],
  };
}

// ── voice profiles ───────────────────────────────────────────────────────────

/**
 * Shorter staleness than the broadcast data: a profile changes when an editor changes it, and the
 * queued command list is the feedback that the change landed.
 */
const PROFILE_FRESHNESS = { staleTime: 15_000, retry: STABLE.retry } as const;

/**
 * A create/toggle/remove here takes effect on the box's next poll — there is no queued-command
 * ambiguity like a voice edit has, so a plain invalidate after each mutation is the whole story.
 */
export function useChannels() {
  return useQuery({ queryKey: ['channels'], queryFn: fetchChannels, staleTime: 15_000 });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChannel,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateChannel,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteChannel,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });
}

/**
 * The subscribed sites, and the posts they have brought in.
 *
 * The poller runs every fifteen minutes, so nothing here is worth refetching harder than that —
 * `staleTime` is a minute, which keeps tab switches free without letting a screen go stale past
 * the next pass.
 */
export function useFeedSources() {
  return useQuery({ queryKey: ['feed-sources'], queryFn: fetchFeedSources, staleTime: 60_000 });
}

export function useCreateFeedSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFeedSource,
    // The first read happens inside the create, so the item list is already different.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feed-sources'] });
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    },
  });
}

/**
 * Verifying an address is a mutation, not a query: it is an act an editor takes, with a result
 * they read once, and nothing about it should be cached against a URL they are still typing.
 */
export function usePreviewFeedSource() {
  return useMutation({ mutationFn: previewFeedSource });
}

export function useUpdateFeedSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFeedSource,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['feed-sources'] }),
  });
}

export function useDeleteFeedSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFeedSource,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['feed-sources'] }),
  });
}

export function usePollFeedSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pollFeedSource,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feed-sources'] });
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    },
  });
}

export function useFeedItems(params: { page: number; rowsPerPage: number; sourceId?: string; search?: string }) {
  return useQuery({
    queryKey: ['feed-items', params],
    queryFn: () => fetchFeedItems(params),
    // Paging a list that is already drawn should not blank it; the previous page stays until the
    // next one arrives.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useSpeakerProfiles(includeArchived: boolean) {
  return useQuery({
    queryKey: ['speaker-profiles', includeArchived],
    queryFn: () => fetchSpeakerProfiles(includeArchived),
    ...PROFILE_FRESHNESS,
  });
}

export function useSpeakerProfile(profileId: string) {
  return useQuery({
    queryKey: ['speaker-profile', profileId],
    queryFn: () => fetchSpeakerProfile(profileId),
    enabled: Boolean(profileId),
    ...PROFILE_FRESHNESS,
  });
}

/**
 * Queues a profile edit and refetches both the list and the open profile, so the pending command
 * appears immediately. Nothing is optimistically applied: the pipeline has not run yet, and
 * showing a rename as done before it happened would be a lie about the system's state.
 */
export function useCreateSpeakerProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSpeakerProfile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['speaker-profiles'] });
    },
  });
}

/** Refetches the profile it belongs to, where the import shows up as a queued command. */
export function useUploadSpeakerClip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadSpeakerClip,
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['speaker-profile', variables.profileId] });
      void queryClient.invalidateQueries({ queryKey: ['speaker-profiles'] });
    },
  });
}

export function useSubmitSpeakerCommand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitSpeakerCommand,
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['speaker-profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['speaker-profile', variables.profileId] });
    },
  });
}

// ── processing queue ─────────────────────────────────────────────────────────

/**
 * A job in flight is the one thing on the dashboard that changes on its own, so it is the one
 * thing that polls. Ten seconds is slower than a stage boundary and fast enough that "empezó a
 * transcribir" appears while the editor is still looking at the screen.
 */
const JOB_POLL_MS = 10_000;

function hasWorkInFlight(jobs: PipelineJob[] | undefined): boolean {
  return (jobs ?? []).some(job => job.status === 'pending' || job.status === 'running');
}

export function usePipelineJobs(input: { streamId?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['pipeline-jobs', input.streamId ?? '', input.limit ?? 25],
    queryFn: () => fetchPipelineJobs(input),
    staleTime: 5_000,
    retry: STABLE.retry,
    // Only while something is actually running: a settled queue is as static as any other list.
    refetchInterval: query => (hasWorkInFlight(query.state.data?.jobs) ? JOB_POLL_MS : false),
  });
}

/**
 * Queues a run and refreshes the queue. Nothing is applied optimistically — the stages have not
 * started, and the box may still be booting.
 */
export function useSubmitPipelineJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitPipelineJob,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pipeline-jobs'] });
    },
  });
}

// ── people ───────────────────────────────────────────────────────────────────

/** Only an admin can read this; an editor gets a 403 and the screen is not in their rail. */
export function usePeople() {
  return useQuery({ queryKey: ['people'], queryFn: fetchPeople, staleTime: 30_000, retry: STABLE.retry });
}

export function useSession() {
  return useQuery({ queryKey: ['session'], queryFn: fetchSession, staleTime: 5 * 60_000, retry: false });
}

function usePeopleMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useCreatePerson() {
  return usePeopleMutation(createPerson);
}

export function useResetPersonPassword() {
  return usePeopleMutation(resetPersonPassword);
}

export function useSetPersonStatus() {
  return usePeopleMutation(setPersonStatus);
}


/** The leaderboard. Topics move slowly — a broadcast a day at most — so a short stale window
 *  keeps the screen responsive without asking the server to recompute heat on every render. */
export function useTrendingTopics(input: { category?: string; search?: string; minMentions?: number; page?: number }) {
  return useQuery({
    queryKey: ['topics', input],
    queryFn: () => filterTopics(input),
    staleTime: 30_000,
    // Every filter change is a new key, and a new key has no cached data — so without this the
    // list is torn down to a skeleton and rebuilt on each one. Holding the previous page lets the
    // rows update in place, which is what makes a filter feel like a filter rather than a reload.
    placeholderData: keepPreviousData,
  });
}

export function useCanonicalTopic(topicId: string | undefined) {
  return useQuery({
    queryKey: ['topic', topicId],
    queryFn: () => fetchCanonicalTopic(topicId as string),
    enabled: Boolean(topicId),
    staleTime: 30_000,
  });
}

// ── relevance watches ────────────────────────────────────────────────────────

/**
 * The curves a broadcast opened. Polled while any of them is still open, because a watch's whole
 * point is that it changes after the page was first drawn — but only then: once every watch is
 * closed the answer is final and re-asking it is noise.
 */
export function useRelevanceWatches(streamId: string) {
  return useQuery({
    queryKey: ['relevance-watches', streamId],
    queryFn: () => fetchRelevanceWatches(streamId),
    enabled: Boolean(streamId),
    retry: STABLE.retry,
    staleTime: 60_000,
    refetchInterval: query => (query.state.data?.watches.some(watch => watch.status === 'watching') ? 5 * 60_000 : false),
  });
}

// ── alerts ───────────────────────────────────────────────────────────────────

export function useAlertRules(includeInactive = true) {
  return useQuery({
    queryKey: ['alert-rules', includeInactive],
    queryFn: () => fetchAlertRules(includeInactive),
    staleTime: 30_000,
    retry: STABLE.retry,
  });
}

export function useCreateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAlertRule,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAlertRule,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });
}

export function useDeleteAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAlertRule,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });
}

/**
 * Raised alerts, newest first. Polled on a short interval — this is the one read in the product
 * whose value is that it is current: an alert an editor sees ten minutes late is a broadcast they
 * were not watching.
 *
 * `keepPreviousData` so paging and the poll do not blank the list an editor is reading.
 */
const ALERT_POLL_MS = 20_000;

/**
 * The rail's unread count, as a count and not a page.
 *
 * Its own query key rather than sharing the history screen's: sharing meant the badge paid that
 * screen's page size — fifty full events, excerpts included, every twenty seconds in every open
 * tab — to render one number. The server answers this one with a count and no documents.
 */
export function useUnreadAlertCount(since: string) {
  return useQuery({
    queryKey: ['alert-unread', since],
    queryFn: () => countAlertEventsSince(since),
    enabled: Boolean(since),
    staleTime: 10_000,
    refetchInterval: ALERT_POLL_MS,
    retry: STABLE.retry,
  });
}

export function useAlertEvents(filter: { streamId?: string; ruleId?: string; surface?: EditorialAlertSurface; page?: number; rowsPerPage?: number } = {}) {
  return useQuery({
    queryKey: ['alert-events', filter],
    queryFn: () => filterAlertEvents(filter),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchInterval: ALERT_POLL_MS,
    retry: STABLE.retry,
  });
}

/**
 * What the models have cost over a window of days.
 *
 * `STABLE` rather than a stream's freshness: the box appends rows while a broadcast runs, but
 * nobody reads this screen to watch a bill tick up — they read it to decide which tier deserves
 * an expensive model.
 */
export function useLlmCosts(days: number) {
  return useQuery({ queryKey: ['llm-costs', days], queryFn: () => fetchLlmCosts(days), ...STABLE });
}
